"use client"

import { useEffect, useState } from "react"
import { auth, db } from "../Firebase/firebase-config"
import { ref, get, onValue } from "firebase/database"
import { useNavigate } from "react-router-dom"
import OrderStatus from "../Components/OrderStatus"
import PartnerNavbar from "./partner-navbar"
import ProfileDialog from "./profile-dialog"
import "./partnerStyle.css"

export default function PartnerDashboard() {
  const [orders, setOrders] = useState([])
  const [partnerName, setPartnerName] = useState("")
  const [partnerProfile, setPartnerProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileOpen, setProfileOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    let ordersListener = null
    let profileListener = null

    const loadPartnerOrders = async () => {
      const partnerUid = auth?.currentUser?.uid
      if (!partnerUid) {
        navigate("/partner-login")
        return
      }

      try {
        const profileRef = ref(db, "profileDashboard")
        const profileSnap = await get(profileRef)

        if (!profileSnap.exists()) {
          alert("No profile data found!")
          setLoading(false)
          return
        }

        const profiles = profileSnap.val()
        let mainUserId = null
        let partnerData = null

        // Find main user for this partner
        for (const userId in profiles) {
          if (profiles[userId].partners && profiles[userId].partners[partnerUid]) {
            mainUserId = userId
            partnerData = profiles[userId].partners[partnerUid]
            break
          }
        }

        if (!mainUserId) {
          alert("You are not assigned to any main user!")
          navigate("/partner-login")
          return
        }

        setPartnerName(partnerData?.name || partnerData?.username || "Partner")
        setPartnerProfile(partnerData || null)

        // Listen to orders in real-time
        const ordersRef = ref(db, `orders/${mainUserId}`)
        ordersListener = onValue(ordersRef, (snap) => {
          const data = snap.val()
          setOrders(data ? Object.values(data) : [])
          setLoading(false)
        })

        // Listen to partner removal
        const partnerRef = ref(db, `profileDashboard/${mainUserId}/partners/${partnerUid}`)
        profileListener = onValue(partnerRef, (snap) => {
          if (!snap.exists()) {
            // Partner removed, auto logout
            alert("You are no longer a partner. Logging out...")
            auth.signOut().then(() => {
              navigate("/login", { replace: true })
            })
          }
        })
      } catch (error) {
        console.error("Error loading partner orders:", error)
        setLoading(false)
      }
    }

    loadPartnerOrders()

    // Cleanup listeners
    return () => {
      if (ordersListener) ordersListener()
      if (profileListener) profileListener()
    }
  }, [navigate])

  const handleLogout = async () => {
    await auth.signOut()
    navigate("/partner-login")
  }

  return (
    <main style={{ minHeight: "100dvh", background: "var(--ps-bg)" }}>
      <PartnerNavbar
        title="Partner Dashboard"
        userName={partnerName}
        onProfile={() => setProfileOpen(true)}
        onLogout={handleLogout}
      />

      <section className="ps-container">
        <div className="ps-card" role="region" aria-labelledby="orders-title">
          <div className="ps-card-header">
            <h2 id="orders-title" className="ps-title">
              Your Partner’s Orders
            </h2>
            <p className="ps-subtle">Live updates from your assigned main user.</p>
          </div>
          <div className="ps-card-body">
            {loading ? (
              <p className="ps-muted">Loading...</p>
            ) : orders.length > 0 ? (
              <div className="ps-surface">
                <OrderStatus orders={orders} />
              </div>
            ) : (
              <div className="ps-empty">
                <p className="ps-muted">No orders found.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <ProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        profile={partnerProfile}
        title="Partner Profile"
      />
    </main>
  )
}
